import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getWashPackages } from "./washPackages";
import { auth, db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";

function Services({ user }) {
  const navigate = useNavigate();
  const services = getWashPackages();
  
  // Session management refs
  const isCheckingSession = useRef(false);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  
  // Phone verification alert state
  const [showPhoneVerificationAlert, setShowPhoneVerificationAlert] = useState(false);
  
  // Flag to prevent multiple redirects
  const hasRedirected = useRef(false);
  
  // Helper function to handle redirect cleanup
  const handleRedirectCleanup = () => {
    if (hasRedirected.current) {
      console.log('Already redirected, skipping additional redirect');
      return true; // Already redirected
    }
    
    hasRedirected.current = true;
    return false; // Not redirected yet
  };

  // Check for existing booking sessions
  const checkForExistingSessions = () => {
    if (!auth.currentUser) {
      console.log('No authenticated user, no sessions to check');
      return { hasSession: false, sessionType: null };
    }
    
    console.log('Checking sessions for user:', auth.currentUser.uid);
    
    // Check for global booking session first (persistent across browser restarts)
    const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
    const globalSession = localStorage.getItem(globalSessionKey);
    
    if (globalSession) {
      try {
        const globalSessionData = JSON.parse(globalSession);
        const now = Date.now();
        const age = now - globalSessionData.timestamp;
        
        console.log('Found global session:', globalSessionData.page, 'age:', Math.round(age/1000), 'seconds');
        
        // Ultra aggressive cleanup: 10 seconds for age, 2 seconds for heartbeat
        if (age > 10 * 1000 || (globalSessionData.lastHeartbeat && Date.now() - globalSessionData.lastHeartbeat > 2 * 1000)) {
          console.log('Global session is stale, removing');
          localStorage.removeItem(globalSessionKey);
        } else {
          // Additional validation: ensure session has required fields
          if (globalSessionData.page && globalSessionData.timestamp && (globalSessionData.page === 'appointment' || globalSessionData.page === 'payment')) {
            console.log('Active global session found - BLOCKING');
            return { hasSession: true, sessionType: globalSessionData.page }; // Return session type
          } else {
            console.log('Invalid global session data, removing');
            localStorage.removeItem(globalSessionKey);
          }
        }
      } catch (error) {
        console.log('Invalid global session data, removing');
        localStorage.removeItem(globalSessionKey);
      }
    }
    
    // Check for appointment page session (sessionStorage - current session only)
    const appointmentSessionKey = `appointment_session_${auth.currentUser.uid}`;
    const appointmentSession = sessionStorage.getItem(appointmentSessionKey);
    
    if (appointmentSession) {
      try {
        const sessionData = JSON.parse(appointmentSession);
        const now = Date.now();
        const age = now - sessionData.timestamp;
        
        console.log('Found appointment session:', sessionData.page, 'age:', Math.round(age/1000), 'seconds');
        
        // Ultra aggressive cleanup: 2 seconds instead of 5, or if age is 0 or 1 (just created)
        if (age > 2 * 1000 || age === 0 || age === 1000) {
          console.log('Appointment session is stale or just created (0-1 seconds), removing');
          sessionStorage.removeItem(appointmentSessionKey);
        } else {
          // Additional validation: ensure session has required fields
          if (sessionData.page && sessionData.timestamp && sessionData.page === 'appointment') {
            console.log('Active appointment session found - BLOCKING');
            return { hasSession: true, sessionType: 'appointment' }; // Return session type
          } else {
            console.log('Invalid appointment session data, removing');
            sessionStorage.removeItem(appointmentSessionKey);
          }
        }
      } catch (error) {
        console.log('Invalid appointment session data, removing');
        sessionStorage.removeItem(appointmentSessionKey);
      }
    }
    
    // Check for payment page session (sessionStorage - current session only)
    const paymentSessionKey = `payment_session_${auth.currentUser.uid}`;
    const paymentSession = sessionStorage.getItem(paymentSessionKey);
    
    if (paymentSession) {
      try {
        const sessionData = JSON.parse(paymentSession);
        const now = Date.now();
        const age = now - sessionData.timestamp;
        
        console.log('Found payment session:', sessionData.page, 'age:', Math.round(age/1000), 'seconds');
        
        // Ultra aggressive cleanup: 2 seconds instead of 5, or if age is 0 or 1 (just created)
        if (age > 2 * 1000 || age === 0 || age === 1000) {
          console.log('Payment session is stale or just created (0-1 seconds), removing');
          sessionStorage.removeItem(paymentSessionKey);
        } else {
          // Additional validation: ensure session has required fields
          if (sessionData.page && sessionData.timestamp && sessionData.page === 'payment') {
            console.log('Active payment session found - BLOCKING');
            return { hasSession: true, sessionType: 'payment' }; // Return session type
          } else {
            console.log('Invalid payment session data, removing');
            sessionStorage.removeItem(paymentSessionKey);
          }
        }
      } catch (error) {
        console.log('Invalid payment session data, removing');
        sessionStorage.removeItem(paymentSessionKey);
      }
    }
    
    console.log('No active sessions found');
    return { hasSession: false, sessionType: null }; // No active sessions
  };

  // Clean up stale sessions on component mount
  useEffect(() => {
    if (!auth.currentUser) return;
    
    console.log('Services component mounted - cleaning up stale sessions');
    
    // Clean up stale global sessions
    const globalSessionKey = `global_booking_session_${auth.currentUser.uid}`;
    const globalSession = localStorage.getItem(globalSessionKey);
    if (globalSession) {
      try {
        const globalSessionData = JSON.parse(globalSession);
        const age = Date.now() - globalSessionData.timestamp;
        
        if (age > 10 * 60 * 1000) {
          localStorage.removeItem(globalSessionKey);
          console.log('Stale global session cleaned up on Services mount');
        }
      } catch (error) {
        localStorage.removeItem(globalSessionKey);
        console.log('Invalid global session cleaned up on Services mount');
      }
    }
    
    // Clean up stale browser sessions
    const appointmentSessionKey = `appointment_session_${auth.currentUser.uid}`;
    const paymentSessionKey = `payment_session_${auth.currentUser.uid}`;
    
    [appointmentSessionKey, paymentSessionKey].forEach(key => {
      const session = sessionStorage.getItem(key);
      if (session) {
        try {
          const sessionData = JSON.parse(session);
          const age = Date.now() - sessionData.timestamp;
          
          if (age > 5 * 60 * 1000) {
            sessionStorage.removeItem(key);
            console.log('Stale browser session cleaned up on Services mount:', key);
          }
        } catch (error) {
          sessionStorage.removeItem(key);
          console.log('Invalid browser session cleaned up on Services mount:', key);
        }
      }
    });
  }, []);

  // Listen for session changes in other tabs
  useEffect(() => {
    if (!auth.currentUser) return;
    
    const handleStorageChange = (e) => {
      const appointmentSessionKey = `appointment_session_${auth.currentUser.uid}`;
      const paymentSessionKey = `payment_session_${auth.currentUser.uid}`;
      
      // If a session is created in another tab, show a notification
      if ((e.key === appointmentSessionKey || e.key === paymentSessionKey) && e.newValue) {
        try {
          const sessionData = JSON.parse(e.newValue);
          console.log('Booking session started in another tab:', sessionData.page);
        } catch (error) {
          console.warn('Error parsing session data:', error);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      isCheckingSession.current = false;
    };
  }, []);

  // Handle appointment selection with session checking
  const handleAppointmentSelection = () => {
    console.log('Appointment selected - checking for existing sessions...');
    
    // Check for existing sessions before navigating
    const sessionCheck = checkForExistingSessions();
    console.log('Session check result:', sessionCheck);
    
    if (sessionCheck.hasSession) {
      console.log('CONFLICT DETECTED - blocking navigation for:', sessionCheck.sessionType);
      
      // Force cleanup any existing sessions before showing error
      if (window.forceCleanupAppointmentSession) {
        window.forceCleanupAppointmentSession();
      }
      
      // Add a small delay to ensure cleanup completes
      setTimeout(() => {
        // Use the specific session type for more accurate error message
        if (sessionCheck.sessionType === 'payment') {
          if (handleRedirectCleanup()) {
            return;
          }
          alert('A payment page is already open. Redirecting to services page.');
          navigate('/services');
        } else if (sessionCheck.sessionType === 'appointment') {
          if (handleRedirectCleanup()) {
            return;
          }
          alert('An appointment page is already open. Redirecting to services page.');
          navigate('/services');
        } else {
          if (handleRedirectCleanup()) {
            return;
          }
          alert('You already have an active booking session. Redirecting to services page.');
          navigate('/services');
        }
      }, 100); // 100ms delay to ensure cleanup
      
      return;
    }
    
    console.log('No conflicts - proceeding to appointment page');
    
    // Navigate to appointment page with service data
    navigate('/appointment', { 
      state: { 
        serviceData: {
          name: selectedService.name,
          price: selectedService.price.replace('$', ''),
          description: selectedService.description
        }
      }
    });
    
    // Close modal
    setShowModal(false);
    setSelectedService(null);
  };

  // Handle drive-in selection with session checking
  const handleDriveInSelection = () => {
    console.log('Drive-in selected - checking for existing sessions...');
    
    // Check for existing sessions before navigating
    const sessionCheck = checkForExistingSessions();
    console.log('Session check result:', sessionCheck);
    
    if (sessionCheck.hasSession) {
      console.log('CONFLICT DETECTED - blocking navigation for:', sessionCheck.sessionType);
      
      // Force cleanup any existing sessions before showing error
      if (window.forceCleanupAppointmentSession) {
        window.forceCleanupAppointmentSession();
      }
      
      // Add a small delay to ensure cleanup completes
      setTimeout(() => {
        // Use the specific session type for more accurate error message
        if (sessionCheck.sessionType === 'payment') {
          if (handleRedirectCleanup()) {
            return;
          }
          alert('A payment page is already open. Redirecting to services page.');
          navigate('/services');
        } else if (sessionCheck.sessionType === 'appointment') {
          if (handleRedirectCleanup()) {
            return;
          }
          alert('An appointment page is already open. Redirecting to services page.');
          navigate('/services');
        } else {
          if (handleRedirectCleanup()) {
            return;
          }
          alert('You already have an active booking session. Redirecting to services page.');
          navigate('/services');
        }
      }, 100); // 100ms delay to ensure cleanup
      
      return;
    }
    
    console.log('No conflicts - proceeding to payment page');
    
    // Navigate to payment page with service data in the format expected by Payment component
    navigate('/payment', { 
      state: { 
        name: selectedService.name,
        price: selectedService.price.replace('$', ''),
        description: selectedService.description,
        isDriveIn: true
      }
    });
    
    // Close modal
    setShowModal(false);
    setSelectedService(null);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedService(null);
  };

  // Check if user's phone number is verified
  const checkPhoneVerification = async () => {
    if (!user) {
      return false;
    }
    
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.phoneVerified === true;
      }
      return false;
    } catch (error) {
      console.error('Error checking phone verification:', error);
      return false;
    }
  };

  // Handle service selection - show modal immediately without session checking
  const handleServiceSelection = async (service) => {
    if (!user) {
      // User is not logged in, redirect to auth page with redirect to services
      navigate('/auth', { state: { redirectPath: '/services' } });
      return;
    }
    
    // Check if phone is verified
    const isPhoneVerified = await checkPhoneVerification();
    if (!isPhoneVerified) {
      setShowPhoneVerificationAlert(true);
      return;
    }
    
    console.log('=== SERVICE SELECTION ===');
    console.log('Showing service options modal');
    
    // Show the modal with service options immediately
    setSelectedService(service);
    setShowModal(true);
  };

  return (
    <div>
      {/* Hero Section */}
      <div style={{
        background: '#1f2937',
        color: '#fff',
        padding: '4rem 2rem',
        textAlign: 'center'
      }}>
        <h1 style={{
          fontSize: '3rem',
          fontWeight: 'bold',
          marginBottom: '1rem',
          color: '#f97316'
        }}>
          Our Services
        </h1>
        <p style={{
          fontSize: '1.2rem',
          maxWidth: '600px',
          margin: '0 auto',
          lineHeight: '1.6'
        }}>
          Professional Auto Wash service that specializes in External Washes. Touchless, fast, convenient, safe and reliable.
        </p>
      </div>

      {/* Services Grid */}
      <div style={{
        padding: '3rem 2rem',
        background: '#f9fafb'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          {/* Pricing & payment notice */}
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '2rem',
            marginBottom: '2rem',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            border: '2px solid #f97316',
            gridColumn: '1 / -1',
            textAlign: 'center'
          }}>
            <div style={{
              color: 'red',
              fontSize: '1.43rem',
              fontWeight: 500,
              marginBottom: '0.5rem'
            }}>
              Free Online Appointments
            </div>
            <div style={{
              color: 'red',
              fontSize: '1.43rem',
              fontWeight: 500,
              marginBottom: '0.5rem'
            }}>
              Plus $5 to $10 for large vehicles
            </div>
            <div style={{
              color: 'red',
              fontSize: '1.43rem',
              fontWeight: 500,
              marginBottom: '0.5rem'
            }}>
              All fees are paid at location
            </div>
            <div style={{
              color: 'red',
              fontSize: '1.43rem',
              fontWeight: 500
            }}>
              Wash type can be changed at location
            </div>
          </div>

          {services.map((service, index) => (
            <div key={index} 
              style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '2rem',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                border: '2px solid #e5e7eb',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                cursor: 'pointer',
                minHeight: '400px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
              onMouseOver={e => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                e.currentTarget.style.borderColor = '#f97316';
              }}
              onMouseOut={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              <div style={{
                fontSize: '3rem',
                textAlign: 'center',
                marginBottom: '1rem'
              }}>
                {service.icon}
              </div>
              
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#1f2937',
                marginBottom: '0.5rem',
                textAlign: 'center'
              }}>
                {service.name}
              </h3>
              
              <p style={{
                color: '#6b7280',
                marginBottom: '1rem',
                textAlign: 'center',
                lineHeight: '1.5'
              }}>
                {service.description}
              </p>
              
              <div style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: service.isOnSale ? '#dc2626' : '#f97316',
                textAlign: 'center',
                marginBottom: '1.5rem'
              }}>
                {service.isOnSale && (
                  <div style={{
                    fontSize: '1rem',
                    color: '#9ca3af',
                    textDecoration: 'line-through',
                    fontWeight: '500',
                    marginBottom: '0.15rem',
                  }}>
                    {service.regularPrice}
                  </div>
                )}
                {service.price}
                {service.isOnSale && (
                  <div style={{ fontSize: '0.85rem', color: '#dc2626', marginTop: '0.25rem' }}>
                    July Special
                  </div>
                )}
              </div>
              
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {service.features.map((feature, featureIndex) => (
                  <li key={featureIndex} style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '0.5rem',
                    color: '#4b5563',
                    background: 'transparent',
                    transition: 'none',
                    pointerEvents: 'none'
                  }}>
                    <span style={{
                      color: '#f97316',
                      marginRight: '0.5rem',
                      fontSize: '1.1rem',
                      background: 'transparent',
                      transition: 'none',
                      pointerEvents: 'none'
                    }}>
                      {feature.included ? '✓' : '✗'}
                    </span>
                    {feature.name}
                  </li>
                ))}
              </ul>
              
              <button 
                onClick={() => handleServiceSelection(service)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#f97316',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  marginTop: '1.5rem',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#ea580c'}
                onMouseOut={(e) => e.target.style.background = '#f97316'}
              >
                BOOK NOW
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Service Options Modal */}
      {showModal && selectedService && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            textAlign: 'center'
          }}>
            <h2 style={{
              color: '#1f2937',
              fontSize: '1.8rem',
              fontWeight: 'bold',
              marginBottom: '1rem'
            }}>
              Choose Your Service Type
            </h2>
            
            <div style={{
              background: '#f9fafb',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem'
            }}>
              <h3 style={{
                color: '#f97316',
                fontSize: '1.3rem',
                fontWeight: 'bold',
                marginBottom: '0.5rem'
              }}>
                {selectedService.name}
              </h3>
              <p style={{
                color: '#6b7280',
                marginBottom: '0.5rem'
              }}>
                {selectedService.description}
              </p>
              <div style={{
                color: selectedService.isOnSale ? '#dc2626' : '#f97316',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}>
                {selectedService.isOnSale && (
                  <span style={{
                    display: 'block',
                    fontSize: '1rem',
                    color: '#9ca3af',
                    textDecoration: 'line-through',
                    fontWeight: '500',
                  }}>
                    {selectedService.regularPrice}
                  </span>
                )}
                {selectedService.price}
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <button
                onClick={handleAppointmentSelection}
                style={{
                  padding: '1rem',
                  background: '#f97316',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#ea580c';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#f97316';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <span style={{ fontSize: '1.5rem' }}>📅</span>
                <span>Appointment</span>
                <span style={{ fontSize: '0.8rem', opacity: 0.9 }}>Schedule a time</span>
              </button>

              <button
                onClick={handleDriveInSelection}
                disabled={true}
                style={{
                  padding: '1rem',
                  background: '#e5e7eb',
                  color: '#9ca3af',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'not-allowed',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#059669';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#10b981';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <span style={{ fontSize: '1.5rem' }}>🚗</span>
                <span>Drive In</span>
                <span style={{ fontSize: '0.8rem', opacity: 0.9 }}>Wait in line</span>
              </button>
            </div>

            <button
              onClick={handleCloseModal}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'transparent',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#f3f4f6';
                e.target.style.color = '#374151';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = '#6b7280';
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Phone Verification Alert Modal */}
      {showPhoneVerificationAlert && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          }}>
            <h3 style={{ 
              color: '#dc2626', 
              marginBottom: '1rem', 
              fontSize: '1.5rem', 
              fontWeight: 'bold' 
            }}>
              Phone Verification Required
            </h3>
            <p style={{ 
              color: '#4b5563', 
              marginBottom: '1.5rem', 
              fontSize: '1rem', 
              lineHeight: '1.5' 
            }}>
              You must verify your phone number before booking an appointment. Please go to your account page to verify your contact number.
            </p>
            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              justifyContent: 'flex-end' 
            }}>
              <button
                onClick={() => setShowPhoneVerificationAlert(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'transparent',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#f3f4f6';
                  e.target.style.color = '#374151';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#6b7280';
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowPhoneVerificationAlert(false);
                  navigate('/account');
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f97316',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#ea580c';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#f97316';
                }}
              >
                Go to Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Services; 